import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ChannelAlert } from '../lib/api';

/**
 * Channel problems, across the top of every tab.
 *
 * It sits here rather than on the admin screen because of what it is warning
 * about. eBay's authorization lasts 18 months and cannot be renewed without a
 * person; the old warning went to the container log and a settings page nobody
 * opens, so the first real signal would have been prices quietly going stale.
 *
 * Visible to every role for the same reason: a viewer reading a price is the
 * one who most needs to know that column stopped updating a week ago.
 */

const HEADLINE: Record<ChannelAlert['kind'], string> = {
  blocked: 'not working',
  expiring: 'authorization expiring',
  sync_failed: 'sync failing',
  sales_sync_failed: 'sales import failing',
};

export function AlertBanner({ onOpenAdmin }: { onOpenAdmin?: () => void }) {
  const [dismissed, setDismissed] = useState(false);

  const { data } = useQuery({
    queryKey: ['alerts'],
    queryFn: api.alerts,
    // Cheap, and the point is to notice without a reload.
    refetchInterval: 5 * 60 * 1000,
    throwOnError: false,
  });

  const alerts = data?.alerts ?? [];
  if (alerts.length === 0 || dismissed) return null;

  // A blocked channel colours the whole banner: if anything is outright broken,
  // the softer expiring notices should not set the tone.
  const worst = alerts.some((alert) => alert.kind === 'blocked') ? 'error' : 'warn';
  const needsAdmin = alerts.filter((alert) => alert.actionRequired).length;

  return (
    <div className={`alert ${worst} alert-banner`}>
      <div>
        <strong>
          {alerts.length === 1
            ? `${alerts[0]!.channelName}: ${HEADLINE[alerts[0]!.kind]}`
            : `${alerts.length} channel problems`}
          {/* Whether anyone has to act, stated up front. An Etsy authorization
              renews itself on the next sync; an eBay one stops the channel dead
              unless someone re-consents. Reading identically was the problem. */}
          {needsAdmin === 0 ? (
            <span className="small muted"> — no action needed</span>
          ) : (
            <span className="small"> — {needsAdmin} need{needsAdmin === 1 ? 's' : ''} an admin</span>
          )}
        </strong>
        <ul>
          {alerts.map((alert, index) => (
            <li key={`${alert.channelId}:${alert.kind}:${index}`}>
              {alert.actionRequired ? (
                <strong>{alert.message}</strong>
              ) : (
                <span className="muted">{alert.message}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="spacer" />
      {onOpenAdmin && (
        <button className="link small" onClick={onOpenAdmin}>
          Open Admin
        </button>
      )}
      {/* Dismissable for this page load only — never persisted. A warning you
          can permanently silence is a warning that will be silenced. */}
      <button className="link small" onClick={() => setDismissed(true)} title="Hides until reload">
        hide
      </button>
    </div>
  );
}
